import { gql } from '@apollo/client'

export const PROVIDER_FOR_BOOKING_QUERY = gql`
  query ProviderForBooking($id: ID!, $availabilityFrom: ISO8601Date!, $availabilityTo: ISO8601Date!) {
    providerProfile(id: $id) {
      id
      bio
      yearsExperience
      hourlyRateCents
      verified
      onVacation
      vacationUntil
      averageRating
      totalReviews
      serviceAreas {
        id
        postalCode
        areaName
        city
      }
      user {
        firstName
        lastName
        avatarUrl
      }
      serviceOfferings {
        id
        customPriceCents
        customDurationMinutes
        serviceType {
          id
          name
          nameEl
          translationKey
          basePriceCents
          estimatedDurationMinutes
          serviceCategory {
            id
            name
            nameEl
            translationKey
          }
        }
      }
      availableSlots(from: $availabilityFrom, to: $availabilityTo) {
        date
        startTime
        endTime
        source
      }
    }
  }
`

export const MY_PROPERTIES_QUERY = gql`
  query MyPropertiesForBooking {
    myProperties {
      id
      label
      propertyType
      addressLine1
      city
      postalCode
      country
      floor
      hasElevator
      sizeSqm
      bedrooms
      bathrooms
      isDefault
    }
  }
`

export const CREATE_PROPERTY_MUTATION = gql`
  mutation CreatePropertyForBooking($input: CreatePropertyInput!) {
    createProperty(input: $input) {
      property {
        id
        label
        propertyType
        addressLine1
        city
        postalCode
        country
        floor
        hasElevator
        sizeSqm
        bedrooms
        bathrooms
        isDefault
      }
      errors
    }
  }
`

export const CREATE_BOOKING_MUTATION = gql`
  mutation CreateBookingFromFlow($input: CreateBookingInput!) {
    createBooking(input: $input) {
      booking {
        id
        status
        scheduledDate
        startTime
        endTime
        durationMinutes
        priceCents
        paymentMethod
        customerNotes
        providerProfile {
          id
          user {
            firstName
            lastName
            avatarUrl
          }
        }
        serviceType {
          id
          name
          nameEl
          translationKey
        }
        property {
          id
          label
          addressLine1
          city
          postalCode
        }
      }
      errors
    }
  }
`

export interface BookingProviderUser {
  firstName: string
  lastName: string
  avatarUrl: string | null
}

export interface BookingServiceCategory {
  id: string
  name: string
  nameEl: string
  translationKey: string
}

export interface BookingServiceType {
  id: string
  name: string
  nameEl: string
  translationKey: string
  basePriceCents: number
  estimatedDurationMinutes: number
  serviceCategory: BookingServiceCategory
}

export interface BookingServiceOffering {
  id: string
  customPriceCents: number | null
  customDurationMinutes: number | null
  serviceType: BookingServiceType
}

export interface BookingServiceArea {
  id: string
  postalCode: string
  areaName: string | null
  city: string
}

export interface BookingAvailableSlot {
  date: string
  startTime: string
  endTime: string
  source: string
}

export interface BookingProviderProfile {
  id: string
  bio: string | null
  yearsExperience: number | null
  hourlyRateCents: number | null
  verified: boolean
  onVacation: boolean
  vacationUntil: string | null
  averageRating: number
  totalReviews: number
  serviceAreas: BookingServiceArea[]
  user: BookingProviderUser
  serviceOfferings: BookingServiceOffering[]
  availableSlots: BookingAvailableSlot[]
}

export interface ProviderForBookingData {
  providerProfile: BookingProviderProfile | null
}

export interface ProviderForBookingVariables {
  id: string
  availabilityFrom: string
  availabilityTo: string
}

export interface BookingProperty {
  id: string
  label: string
  propertyType: string
  addressLine1: string
  city: string | null
  postalCode: string
  country: string
  floor: number | null
  hasElevator: boolean
  sizeSqm: number | null
  bedrooms: number | null
  bathrooms: number | null
  isDefault: boolean
}

export interface MyPropertiesData {
  myProperties: BookingProperty[]
}

export interface CreatePropertyInput {
  label: string
  propertyType: string
  addressLine1: string
  city: string
  postalCode: string
  country: string
  hasElevator: boolean
  isDefault: boolean
}

export interface CreatePropertyData {
  createProperty: {
    property: BookingProperty | null
    errors: string[]
  }
}

export interface CreatePropertyVariables {
  input: CreatePropertyInput
}

export interface CreatedBooking {
  id: string
  status: string
  scheduledDate: string
  startTime: string
  endTime: string
  durationMinutes: number
  priceCents: number
  paymentMethod: string | null
  customerNotes: string | null
  providerProfile: {
    id: string
    user: BookingProviderUser
  }
  serviceType: {
    id: string
    name: string
    nameEl: string
    translationKey: string
  }
  property: {
    id: string
    label: string
    addressLine1: string
    city: string | null
    postalCode: string
  } | null
}

export interface CreateBookingData {
  createBooking: {
    booking: CreatedBooking | null
    errors: string[]
  }
}

export interface CreateBookingInput {
  providerProfileId: string
  serviceTypeId: string
  propertyId: string
  scheduledDate: string
  startTime: string
  durationMinutes: number
  customerNotes?: string
  paymentMethod: 'card' | 'cash'
}

export interface CreateBookingVariables {
  input: CreateBookingInput
}
